import React, { useEffect } from 'react';
import { Clock, CreditCard } from 'lucide-react';

/**
 * HoldBookingToggle — reusable Hold-vs-Instant chooser.
 *
 * Sprint H1+H2: shows on Review/Ancillary pages when the supplier
 * indicates the fare is hold-eligible.
 *
 * Props:
 *   eligible    bool — render only when true (controlled by caller)
 *   value       'instant' | 'hold'
 *   onChange    (mode) => void
 *   provider    'tripjack' | 'riya' (sub-text copy hint, optional)
 *
 * Persists user's last choice in localStorage so repeat agents don't have
 * to re-pick on every booking.
 */
const STORAGE_KEY = 'acf_hold_booking_pref';

const HoldBookingToggle = ({ eligible, value, onChange, provider }) => {
  // On first mount, restore last preference from localStorage if caller didn't set
  useEffect(() => {
    if (!eligible || value) return;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'hold' || saved === 'instant') onChange(saved);
      else onChange('instant');
    } catch {
      onChange('instant');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible]);

  const setMode = (m) => {
    onChange(m);
    try { localStorage.setItem(STORAGE_KEY, m); } catch { /* ignore */ }
  };

  if (!eligible) return null;

  const cards = [
    {
      key: 'instant',
      icon: CreditCard,
      title: 'Pay Now & Confirm Ticket',
      sub: 'Lock the fare and ticket immediately. Recommended for confirmed travel plans.',
    },
    {
      key: 'hold',
      icon: Clock,
      title: 'Hold This Fare',
      sub: 'Lock the seat for approximately 8 hours. Pay & confirm later from your bookings page.',
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="hold-booking-toggle">
      <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-3">
        Choose Booking Type
      </h3>
      {/* Sprint May-2026 · Always single-column. The widget renders in
          BookingPage's narrow right-rail (~280-360px); a 2-col grid forced
          each card to ~140px wide and crammed titles into 4-line stacks
          with the "Selected" badge overlapping the description. Single
          column keeps the cards readable at every breakpoint. */}
      <div className="flex flex-col gap-3">
        {cards.map((card) => {
          const Icon = card.icon;
          const active = value === card.key;
          return (
            <button
              key={card.key}
              type="button"
              data-testid={`booking-mode-${card.key}`}
              onClick={() => setMode(card.key)}
              className={`relative text-left rounded-xl border p-4 transition-all ${
                active
                  ? 'border-[#659A9B] bg-[#F0F8F8] shadow-sm ring-1 ring-[#659A9B]/30'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              {/* Selected ribbon — corner pill, never collides with title text */}
              {active && (
                <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider text-white bg-[#659A9B] px-2 py-0.5 rounded-full">
                  Selected
                </span>
              )}
              <div className="flex items-start gap-3">
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    active ? 'bg-[#659A9B] text-white' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0 pr-16">
                  <div className={`text-sm font-semibold leading-tight ${active ? 'text-[#3D5F60]' : 'text-gray-800'}`}>
                    {card.title}
                  </div>
                  <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">{card.sub}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {value === 'hold' && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-3">
          <strong>Note:</strong> Hold bookings auto-cancel if not confirmed within ~8 hours. Pay from your bookings dashboard before the timer runs out.
          {provider === 'riya' && ' (Some airlines have shorter hold windows; check the email confirmation.)'}
        </p>
      )}
    </div>
  );
};

export default HoldBookingToggle;
